import React, { useEffect, useMemo, useState } from "react";
import Loading from "../../../../components/Loading";

import {
  Overlay,
  ModalContainer,
  ModalHeader,
  ModalBody,
  FilesList,
  FileItem,
  FileInfo,
  FileActions,
  PreviewContainer,
  CloseButton,
  LinkActionsRow,
  LinkButton,
  LinkBox,
  CopyButton,
  EmptyState,
  InfoGrid,
  InfoItem,
  Divider,
} from "./styles";

import { FiX, FiEye, FiDownload, FiTrash2, FiLink2, FiCopy, FiCheck } from "react-icons/fi";
import api from "../../../../services/api";
import { ConfirmDialog } from "../ConfirmDialog";

interface Props {
  rentProcessId: number;
  onClose: () => void;
}

interface IUserFile {
  id: number;
  original_name: string;
  mime_type: string;
}

type RentProcessDoc = {
  id: number;
  profession: string | null;
  material_status: string | null;
  express_offer: number;
  created_at: string;
  files?: IUserFile[];
};

type RentProcessData = {
  id: number;
  guarantee?: { id: number; name: string } | null;
  documents?: RentProcessDoc[];
};

interface IRentProcessResponse {
  data: RentProcessData[];
}

export default function DocumentsModal({ rentProcessId, onClose }: Props) {
  const [loading, setLoading] = useState(true);

  const [rentProcess, setRentProcess] = useState<RentProcessData | null>(null);
  const [files, setFiles] = useState<IUserFile[]>([]);
  const [previewFile, setPreviewFile] = useState<(IUserFile & { url: string }) | null>(null);

  // delete
  const [showConfirm, setShowConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<IUserFile | null>(null);
  const [deleting, setDeleting] = useState(false);

  // link de envio
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const hasDocuments = useMemo(() => files.length > 0, [files]);

  const latestDoc = useMemo(() => {
    const docs = rentProcess?.documents ?? [];
    if (docs.length === 0) return null;

    return [...docs].sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0];
  }, [rentProcess]);

  const guaranteeName = rentProcess?.guarantee?.name ?? "—";
  const civilState = latestDoc?.material_status ?? "—";
  const profession = latestDoc?.profession ?? "—";

  useEffect(() => {
    async function loadFilesAndProcess() {
      setLoading(true);
      setPreviewFile(null);

      try {
        const response = await api.get<IRentProcessResponse>("/api/adm/documents/files", {
          params: { rent_process_id: rentProcessId, page: 1 },
        });

        const rp = response.data.data?.[0] ?? null;
        setRentProcess(rp);

        const flattenedFiles =
          rp?.documents?.flatMap((d) => d.files ?? []) ?? [];

        setFiles(flattenedFiles);
      } catch (err) {
        console.error(err);
        setRentProcess(null);
        setFiles([]);
      } finally {
        setLoading(false);
      }
    }

    loadFilesAndProcess();
  }, [rentProcessId]);

  async function getFileUrl(fileId: number, download = false) {
    const response = await api.get(`/api/adm/documents/files/${fileId}`, {
      params: download ? { download: 1 } : {},
    });

    return response.data.url as string;
  }

  async function handlePreview(file: IUserFile) {
    const url = await getFileUrl(file.id);
    setPreviewFile({ ...file, url });
  }

  async function handleDownload(file: IUserFile) {
    const url = await getFileUrl(file.id, true);

    const link = document.createElement("a");
    link.href = url;
    link.download = file.original_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function renderPreview() {
    if (!previewFile) return null;

    if (previewFile.mime_type === "application/pdf") {
      return <iframe src={previewFile.url} title="PDF Preview" />;
    }

    if (previewFile.mime_type.startsWith("image/")) {
      return <img src={previewFile.url} alt={previewFile.original_name} />;
    }

    return <p>Pré-visualização indisponível</p>;
  }

  function askDelete(file: IUserFile) {
    setFileToDelete(file);
    setShowConfirm(true);
  }

  async function confirmDelete() {
    if (!fileToDelete) return;

    try {
      setDeleting(true);

      await api.delete(`/api/adm/documents/files/${fileToDelete.id}`);

      setFiles((prev) => prev.filter((f) => f.id !== fileToDelete.id));
      setPreviewFile(null);

      setShowConfirm(false);
      setFileToDelete(null);
    } catch (err) {
      console.error(err);
      alert("Erro ao excluir documento");
    } finally {
      setDeleting(false);
    }
  }

  async function handleGenerateLink() {
    try {
      setGeneratingLink(true);
      setCopied(false);

      const res = await api.post(`/api/adm/rent-process/${rentProcessId}/document-link`);
      const url = res?.data?.data?.url;

      if (url) setGeneratedLink(url);
      else alert("Não foi possível gerar o link.");
    } catch (err) {
      console.error(err);
      alert("Erro ao gerar link.");
    } finally {
      setGeneratingLink(false);
    }
  }

  async function handleCopyLink() {
    if (!generatedLink) return;

    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error(err);
      alert("Não foi possível copiar o link.");
    }
  }

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ConfirmDialog
          open={showConfirm}
          title="Excluir documento"
          description={
            fileToDelete
              ? `Deseja excluir o arquivo "${fileToDelete.original_name}"? Essa ação não poderá ser desfeita.`
              : ""
          }
          loading={deleting}
          onCancel={() => {
            setShowConfirm(false);
            setFileToDelete(null);
          }}
          onConfirm={confirmDelete}
        />

        <ModalHeader>
          <h3>Documentos do processo</h3>
          <CloseButton onClick={onClose}>
            <FiX size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* ✅ RESUMO DO PROCESSO (GARANTIA / ESTADO CIVIL / OCUPAÇÃO) */}
          <InfoGrid>
            <InfoItem>
              <span>Garantia</span>
              <strong>{guaranteeName}</strong>
            </InfoItem>

            <InfoItem>
              <span>Estado civil</span>
              <strong>{civilState}</strong>
            </InfoItem>

            <InfoItem>
              <span>Ocupação</span>
              <strong>{profession}</strong>
            </InfoItem>
          </InfoGrid>

          <Divider />

          {/* AÇÕES DO LINK */}
          <LinkActionsRow>
            <LinkButton onClick={handleGenerateLink} disabled={generatingLink}>
              <FiLink2 size={16} />
              {generatingLink ? "Gerando..." : "Gerar link de envio"}
            </LinkButton>

            {generatedLink && (
              <>
                <LinkBox title={generatedLink}>{generatedLink}</LinkBox>
                <CopyButton onClick={handleCopyLink} title="Copiar link">
                  {copied ? <FiCheck size={18} /> : <FiCopy size={18} />}
                  {copied ? "Copiado" : "Copiar"}
                </CopyButton>
              </>
            )}
          </LinkActionsRow>

          {loading && <Loading />}

          {!loading && !hasDocuments && (
            <EmptyState>
              <p style={{ fontWeight: "bold", fontSize: "18px" }}>Nenhum documento enviado ainda.</p>
              <p>Você pode gerar um link e enviar ao cliente para ele anexar os documentos.</p>
            </EmptyState>
          )}

          {!loading && hasDocuments && (
            <>
              <FilesList>
                {files.map((file) => (
                  <FileItem key={file.id}>
                    <FileInfo>
                      <strong>{file.original_name}</strong>
                      <span>{file.mime_type}</span>
                    </FileInfo>

                    <FileActions>
                      <FiEye title="Visualizar" onClick={() => handlePreview(file)} />
                      <FiDownload title="Download" onClick={() => handleDownload(file)} />
                      <FiTrash2 title="Excluir" onClick={() => askDelete(file)} />
                    </FileActions>
                  </FileItem>
                ))}
              </FilesList>

              {previewFile && <PreviewContainer>{renderPreview()}</PreviewContainer>}
            </>
          )}
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
}
